import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// Helper function to convert null values to undefined for API compatibility
const sanitizeNullToUndefined = <T extends Record<string, any>>(obj: T): T => {
  const result = {} as T;
  for (const [key, value] of Object.entries(obj)) {
    result[key as keyof T] = value === null ? undefined : value;
  }
  return result;
};
import {
  getEquipmentCatalog,
  getManufacturers,
  getWheelCircumferenceData,
  getRollingRadiiElements,
  addManufacturer,
  addFrame,
  addTyre,
  addWheelFront,
  addWheelRear,
  addEquipmentTeamParameter,
  updateEquipmentTeamParameter,
  deleteEquipmentTeamParameter,
  updateManufacturer,
  updateFrame,
  updateTyre,
  updateWheelFront,
  updateWheelRear,
  deleteManufacturer,
  deleteFrame,
  deleteTyre,
  deleteWheelFront,
  deleteWheelRear,
} from '../api/equipment';
import { EquipmentTyre, EquipmentWheel } from 'types/Equipment';

export const useEquipmentCatalog = () =>
  useQuery({
    queryKey: ['equipment-catalog'],
    queryFn: async () => {
      try {
        const result = await getEquipmentCatalog();
        // If result is an array, return the first element
        // If result is null/undefined/error, return empty object
        if (Array.isArray(result) && result.length > 0) {
          return result[0];
        }
        if (result && !Array.isArray(result)) {
          return result;
        }
        return {};
      } catch (error) {
        console.error('Error fetching equipment catalog:', error);
        return {};
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - equipment data doesn't change often
  });

export const useManufacturers = () =>
  useQuery({
    queryKey: ['manufacturers'],
    queryFn: getManufacturers,
    staleTime: 5 * 60 * 1000, // 5 minutes - manufacturers don't change often
  });

export const useWheelCircumferenceData = () =>
  useQuery({
    queryKey: ['wheel-circumference'],
    queryFn: getWheelCircumferenceData,
    staleTime: 5 * 60 * 1000, // 5 minutes - wheel circumference data doesn't change often
  });

export const useWheelCircumferenceDataFresh = () =>
  useQuery({
    queryKey: ['wheel-circumference-fresh'],
    queryFn: getWheelCircumferenceData,
    staleTime: 0, // Always fetch fresh data
  });

export const useRollingRadiiElements = (sessionIds?: string[]) => {
  // Stable key so swapping session order doesn't cause spurious refetches.
  const sortedIds = sessionIds ? [...sessionIds].sort() : [];
  return useQuery({
    queryKey: ['rolling-radii-elements', sortedIds],
    queryFn: () => getRollingRadiiElements(sortedIds),
    // staleTime 0: the backend scrapes configs synchronously on every request,
    // so callers expect fresh data every mount / invalidation.
    staleTime: 0,
    enabled: sortedIds.length > 0,
  });
};

export const useAddManufacturer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { name: string; notes?: string | null; keep_private?: boolean }) => {
      // Create the manufacturer
      const { keep_private = false, ...manufacturerData } = data;
      const manufacturer = await addManufacturer(sanitizeNullToUndefined(manufacturerData));

      // Create team parameter entry to mark it as private to this team
      await addEquipmentTeamParameter({
        manufacturer_id: manufacturer.id,
        keep_private,
        code: 1000, // EQUIPMENT_ADDITIONS
      });

      return manufacturer;
    },
    onSuccess: () => {
      // Invalidate and refetch both manufacturers and equipment catalog
      queryClient.invalidateQueries({ queryKey: ['manufacturers'] });
      queryClient.invalidateQueries({ queryKey: ['equipment-catalog'] });
    },
  });
};

export const useAddFrame = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      name: string;
      manufacturer_id: string;
      designation?: string | null;
      part_number?: string | null;
      size_nominal?: string | null;
      mass_g?: number | null;
      notes?: string | null;
      keep_private?: boolean;
    }) => {
      // Create the frame
      const { keep_private = false, ...frameData } = data;
      const frame = await addFrame(sanitizeNullToUndefined(frameData));

      // Create team parameter entry to mark it as private to this team
      await addEquipmentTeamParameter({
        frame_id: frame.id,
        keep_private,
        code: 1000, // EQUIPMENT_ADDITIONS
      });

      return frame;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment-catalog'] });
    },
  });
};

export const useAddTyre = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      name: string;
      manufacturer_id: string;
      designation?: string | null;
      part_number?: string | null;
      diameter_mm: number;
      width_mm?: number | null;
      mass_g?: number | null;
      notes?: string | null;
      keep_private?: boolean;
    }) => {
      // Create the tyre
      const { keep_private = false, ...tyreData } = data;
      const tyre = await addTyre(sanitizeNullToUndefined(tyreData));

      // Create team parameter entry to mark it as private to this team
      await addEquipmentTeamParameter({
        tyre_id: tyre.id,
        keep_private,
        code: 1000, // EQUIPMENT_ADDITIONS
      });

      return tyre;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment-catalog'] });
    },
  });
};

export const useAddWheelFront = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      name: string;
      manufacturer_id: string;
      designation?: string | null;
      part_number?: string | null;
      diameter_mm: number;
      depth_mm?: number | null;
      width_inner_mm?: number | null;
      width_outer_mm?: number | null;
      mass_g?: number | null;
      notes?: string | null;
      keep_private?: boolean;
    }) => {
      // Create the wheel
      const { keep_private = false, ...wheelData } = data;
      const wheel = await addWheelFront(sanitizeNullToUndefined(wheelData));

      // Create team parameter entry to mark it as private to this team
      await addEquipmentTeamParameter({
        wheel_front_id: wheel.id,
        keep_private,
        code: 1000, // EQUIPMENT_ADDITIONS
      });

      return wheel;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment-catalog'] });
    },
  });
};

export const useAddWheelRear = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      name: string;
      manufacturer_id: string;
      designation?: string | null;
      part_number?: string | null;
      diameter_mm: number;
      depth_mm?: number | null;
      width_inner_mm?: number | null;
      width_outer_mm?: number | null;
      mass_g?: number | null;
      notes?: string | null;
      keep_private?: boolean;
    }) => {
      // Create the wheel
      const { keep_private = false, ...wheelData } = data;
      const wheel = await addWheelRear(sanitizeNullToUndefined(wheelData));

      // Create team parameter entry to mark it as private to this team
      await addEquipmentTeamParameter({
        wheel_rear_id: wheel.id,
        keep_private,
        code: 1000, // EQUIPMENT_ADDITIONS
      });

      return wheel;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment-catalog'] });
    },
  });
};

// Update hooks
export const useUpdateManufacturer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name: string; notes?: string | null } }) =>
      updateManufacturer(id, sanitizeNullToUndefined(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manufacturers'] });
      queryClient.invalidateQueries({ queryKey: ['equipment-catalog'] });
    },
  });
};

export const useUpdateFrame = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: {
        name: string;
        manufacturer_id: string;
        designation?: string | null;
        part_number?: string | null;
        size_nominal?: string | null;
        mass_g?: number | null;
        notes?: string | null;
      };
    }) => updateFrame(id, sanitizeNullToUndefined(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment-catalog'] });
    },
  });
};

export const useUpdateTyre = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: EquipmentTyre }) => updateTyre(id, sanitizeNullToUndefined(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment-catalog'] });
    },
  });
};

export const useUpdateWheelFront = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: EquipmentWheel }) =>
      updateWheelFront(id, sanitizeNullToUndefined(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment-catalog'] });
    },
  });
};

export const useUpdateWheelRear = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: EquipmentWheel }) =>
      updateWheelRear(id, sanitizeNullToUndefined(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment-catalog'] });
    },
  });
};

// Delete hooks
export const useDeleteManufacturer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteManufacturer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manufacturers'] });
      queryClient.invalidateQueries({ queryKey: ['equipment-catalog'] });
    },
    onError: (error: any) => {
      // Return error for the component to handle
      throw error;
    },
  });
};

export const useDeleteFrame = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteFrame,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment-catalog'] });
    },
    onError: (error: any) => {
      throw error;
    },
  });
};

export const useDeleteTyre = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTyre,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment-catalog'] });
    },
    onError: (error: any) => {
      throw error;
    },
  });
};

export const useDeleteWheelFront = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteWheelFront,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment-catalog'] });
    },
    onError: (error: any) => {
      throw error;
    },
  });
};

export const useDeleteWheelRear = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteWheelRear,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment-catalog'] });
    },
    onError: (error: any) => {
      throw error;
    },
  });
};

// Equipment team parameter update hook
export const useUpdateEquipmentTeamParameter = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { keep_private?: boolean; data?: Record<string, any> } }) =>
      updateEquipmentTeamParameter(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manufacturers'] });
      queryClient.invalidateQueries({ queryKey: ['equipment-catalog'] });
    },
  });
};

export const useDeleteEquipmentTeamParameter = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteEquipmentTeamParameter(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['manufacturers'] });
      queryClient.invalidateQueries({ queryKey: ['equipment-catalog'] });
    },
  });
};
